import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import size from 'lodash/fp/size'
import every from 'lodash/every'
import map from 'lodash/map'

import Fade from '@mui/material/Fade'
import { styled } from '@mui/material/styles'
import { Theme, Box } from '@mui/material'

import RefreshButton from '../buttons/RefreshButton'
import RestoreButton from '../buttons/RestoreButton'
import AppNav from '../AppNav'
import { useTypedSelector } from '../../reducers'

import {
  loadRecycleBinItemsDeletedByMe,
  restoreRecycleBinItemsDeletedByMe
} from '../../actions/recycleBin'

import RecycleBinItemsDeletedByMeTable, {
  canRestoreItem
} from '../RecycleBinItemsDeletedByMeTable'
import { useTablePageParam } from '../../hooks/use-table-page-param.hook'
import { useMeasuredHeight } from '../../hooks/use-measured-height.hook'

type MikeTheme = Theme & {
  palette: Theme['palette'] & {
    lightGrey?: { main: string }
  }
}

const StyledHeader = styled('div')(({ theme }) => {
  const t = theme as MikeTheme
  return {
    position: 'sticky',
    top: theme.spacing(4),
    padding: theme.spacing(2),
    zIndex: 1,
    backgroundColor: t.palette.lightGrey?.main
  }
})

const RecycleBinItemsDeletedByMe = ({ data, totalCount, loading, limit }) => {
  const dispatch = useDispatch()
  const params = useParams<{ id: string }>()
  const tenantIdParam = params.id

  const [selection, setSelection] = useState<any[]>([])
  const [page, setPage] = useTablePageParam()

  useEffect(() => {
    if (tenantIdParam) {
      dispatch(loadRecycleBinItemsDeletedByMe(tenantIdParam))
    }
  }, [dispatch, tenantIdParam])

  const onRefresh = useCallback(() => {
    dispatch(loadRecycleBinItemsDeletedByMe(tenantIdParam))
  }, [dispatch, tenantIdParam])

  const onRestore = useCallback(() => {
    setSelection([])
    const ids = map(selection, 'id')
    dispatch(restoreRecycleBinItemsDeletedByMe(tenantIdParam, ids))
  }, [dispatch, selection, tenantIdParam])

  const onRestoreOneItem = useCallback(
    row => {
      setSelection([])
      if (row.canRestore) {
        const ids = [row.id]
        dispatch(restoreRecycleBinItemsDeletedByMe(tenantIdParam, ids))
      }
    },
    [dispatch, tenantIdParam]
  )

  const onSelectionChange = useCallback(newSelection => {
    setSelection(newSelection)
  }, [])

  const onChangePage = useCallback(
    (newPage, orderBy, order) => {
      dispatch(
        loadRecycleBinItemsDeletedByMe(
          tenantIdParam,
          newPage * limit,
          limit,
          orderBy,
          order
        )
      )
      setPage(newPage)
    },
    [dispatch, limit, tenantIdParam]
  )

  const onChangeRowsPerPage = useCallback(
    (newLimit, orderBy, order) => {
      dispatch(
        loadRecycleBinItemsDeletedByMe(
          tenantIdParam,
          page,
          newLimit,
          orderBy,
          order
        )
      )
    },
    [dispatch, page, tenantIdParam]
  )

  const onHandleRequestSort = useCallback(
    (orderBy, order) => {
      dispatch(
        loadRecycleBinItemsDeletedByMe(
          tenantIdParam,
          page,
          limit,
          orderBy,
          order
        )
      )
    },
    [dispatch, limit, page, tenantIdParam]
  )

  const selectionSize = size(selection)
  const canRestore = selectionSize > 0 && every(selection, canRestoreItem)
  const tenantId = useTypedSelector(state => state.auth.user?.tenantId)

  const [topOffset, measuredRef] = useMeasuredHeight()

  return (
    <>
      <AppNav tenantId={tenantId} />
      <StyledHeader ref={measuredRef}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 4
          }}
        >
          <Fade in={selectionSize > 0}>
            <div>
              <RestoreButton disabled={!canRestore} onClick={onRestore} />
            </div>
          </Fade>
          {/* Match the "New folder" button's outlined style (CreateSubprojectButton). */}
          <RefreshButton
            onClick={onRefresh}
            sx={{
              width: 150,
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: '12px',
              paddingRight: '12px',
              border: '2px solid',
              borderColor: 'secondary.main'
            }}
          />
        </Box>
      </StyledHeader>
      <RecycleBinItemsDeletedByMeTable
        content={data}
        loading={loading}
        selection={selection}
        totalCount={totalCount}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onHandleRequestSort={onHandleRequestSort}
        onRestoreOneItem={onRestoreOneItem}
        onSelectionChange={onSelectionChange}
        page={page}
        topOffset={topOffset}
      />
    </>
  )
}
RecycleBinItemsDeletedByMe.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalCount: PropTypes.number,
  loading: PropTypes.bool.isRequired,
  limit: PropTypes.number
}

export default RecycleBinItemsDeletedByMe
